import dashboardStyle from "./dashboard.module.css";
import emptyStyle from "../Common/emptyState.module.css";

import { apiPentestsCreate, apiPentestsList, apiPentestsDelete, apiJoinPentest } from "../../sdk.ts";

import { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router";
import PentestComponent from "./components/PentestComponent/PentestComponent.tsx";
import InfoHover from "../Common/Tooltips/InformationHover/InformationHover.tsx";

import { FileText, FilePlus, LogIn } from "lucide-react";

import { AuthContext } from "../../contexts/AuthContext.ts";
import { PentestContext } from "../../contexts/PentestContext.ts";

import NewPentestForm from "../Common/Forms/NewPentestForm/NewPentestForm.tsx";
import { AppContext } from "../../contexts/AppContext.ts";
import axios from "axios";
import { TutorialContext } from "../../contexts/TutorialContext.ts";

import { TutorialToggle } from "../LoginChecks/Tutorial/Tutorial.tsx";
import { Pentest } from "../../sdkStructs.ts";
import Button from "../Common/Button/Button.tsx";
import JoinForm from "../Common/Forms/JoinForm/JoinForm.tsx";

type DashboardFormType = "join" | "create" | "closed";

function Dashboard() {
  const navigate = useNavigate();
  const token = useContext(AuthContext).token;
  const { tutorialRefs, nextStep, setStepIndex } = useContext(TutorialContext);
  const setPentestId = useContext(PentestContext).setPentestId;
  const { setLoading, setShowModal, tutorial } = useContext(AppContext);

  const [newPentestForm, setNewPentestForm] = useState<DashboardFormType>("closed");
  const [pastPentests, setPastPentests] = useState<Pentest[]>([]);

  // BACKEND CALLS
  const fetchPastPentests = useCallback(async () => {
    try {
      const response = await apiPentestsList(token);
      const list = response.data;
      list.reverse();
      setPastPentests(list);
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        return;
      }
      console.log("ERROR FETCHING PAST PENTESTS:", error.response);
    }
  }, [token]);

  useEffect(() => {
    if (token !== "") {
      fetchPastPentests();
    }
  }, [token, fetchPastPentests]);

  async function addPentest(data: Pentest) {
    setLoading(true);
    try {
      await apiPentestsCreate(token, data);
      await fetchPastPentests();

      if (tutorial) nextStep();
      setNewPentestForm("closed");
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        return;
      }
      console.log("ERROR ADDING PENTEST:", error.response);
    }
    setLoading(false);
  }

  async function joinPentest(joinToken: string) {
    setLoading(true);
    try {
      await apiJoinPentest(token, joinToken);
      await fetchPastPentests();
      setNewPentestForm("closed");
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        return;
      }
      console.log("ERROR JOINING PENTEST:", error.response);
    }
    setLoading(false);
  }

  async function deletePentestById(id: number) {
    setLoading(true);
    try {
      await apiPentestsDelete(token, id);
      await fetchPastPentests();
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        return;
      }
      console.log("ERROR DELETING PENTEST:", error.response);
    }
    setLoading(false);
  }

  function confirmDeletePentestById(id: number) {
    setShowModal("confirm", {
      text: "Are you sure you want to delete all commands logged in this past pentest?",
      confirm: async () => {
        await deletePentestById(id);
      },
      close: () => setShowModal("none", undefined),
    });
  }

  // REDIRECT
  function openPentest(id: number) {
    setPentestId(id);
    sessionStorage.setItem("pentestId", id.toString());
    setStepIndex(0);
    navigate("/pentest");
  }

  // RENDER COMPONENTS
  function getPastPentests() {
    if (pastPentests.length !== 0)
      return (
        <>
          {pastPentests.map((pentest: Pentest) => (
            <PentestComponent
              owned={false}
              key={pentest.ID}
              data={pentest}
              deleteFunc={confirmDeletePentestById}
              openFunc={() => openPentest(pentest.ID)}
            />
          ))}
        </>
      );

    return (
      <div className={emptyStyle.emptyStateWrapper}>
        <FileText className={emptyStyle.emptyStateImage} stroke="var(--onedark-text)" strokeWidth="1.5" />
        <p className={emptyStyle.emptyTitle}>
          You don't have any reports already written!
          <br />
          Try creating a <b>New pentest</b>.
        </p>
      </div>
    );
  }

  function getCreateColumn() {
    if (newPentestForm === "create")
      return (
        <div className={dashboardStyle.newPentestColumn}>
          <div ref={tutorialRefs.newPentestFormRef}>
            <NewPentestForm submit={addPentest} close={() => setNewPentestForm("closed")} />
          </div>
        </div>
      );

    if (newPentestForm === "join") {
      return (
        <div className={dashboardStyle.newPentestColumn}>
          <JoinForm submit={joinPentest} close={() => setNewPentestForm("closed")} />
        </div>
      );
    }

    return (
      <div className={dashboardStyle.newPentestColumn}>
        <div ref={tutorialRefs.newPentestButtonRef}>
          <Button
            variant="primary"
            textcolor="light"
            morepadding={true}
            onClick={() => {
              if (tutorial) nextStep();
              setNewPentestForm("create");
            }}
          >
            <FilePlus className={dashboardStyle.newFileImage} strokeWidth={1.5} />
            <span className={dashboardStyle.newFileText}>New Pentest</span>
          </Button>
        </div>

        <Button
          variant="secondary"
          textcolor="light"
          morepadding
          onClick={() => {
            setNewPentestForm("join");
          }}
        >
          <LogIn className={dashboardStyle.newFileImage} strokeWidth={1.5} />
          <span className={dashboardStyle.newFileText}>Join Pentest</span>
        </Button>
      </div>
    );
  }

  return (
    <div className={dashboardStyle.dashboardWrapper}>
      <div className={dashboardStyle.listWrapper}>
        <div className={dashboardStyle.pentestListOptions}>
          <h3>Past Pentests</h3>
          <InfoHover description="A list of pentests you created in the past. Click to open up in the Logger." />
        </div>
        <div className={dashboardStyle.pastPentestsColumn}>{getPastPentests()}</div>
      </div>

      {getCreateColumn()}
      <TutorialToggle />
    </div>
  );
}

export default Dashboard;
